import { useMsal } from "@azure/msal-react";
import { loginRequest, logoutRequest } from "./authConfig";
import { useCallback, useEffect, useState } from "react";
import { json, useNavigate } from "react-router-dom";
import { removeLocalStorageItem, setLocalStorageItem } from "../common/utils/local-storage";
import { ACCESS_TOKEN, ACCOUNT, EXPIRES_ON, REFRESH_TOKEN,ID_TOKEN,TIME_OF_AUTO_LOGOUT } from "../common/constants/local-storage-keys";
import { dashboardURL, indexURL} from "../routes/routes";
import Cookies from 'universal-cookie';
import { useSelector } from "react-redux";
import { setSessionFeedbackUrl } from "../redux/actions";
import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import { getBrowserInfo, getAuthenticationStrategy, getCompatibilityMessages } from "../common/utils/browser-detection";


export const useAzureADAuth = () => {
  const { instance } = useMsal();
  const {sessionFeedbackURL}  = useSelector(({ MODULES }) => MODULES);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const cookies = new Cookies();

  // Get browser and platform information
  const browserInfo = getBrowserInfo();
  const authStrategy = getAuthenticationStrategy();
  const compatibilityMessages = getCompatibilityMessages();

  // useEffect(() => {
  //     const userInformation = sessionStorage.getItem('msal.token.keys.' + process.env.REACT_APP_CLIENT_ID);
  //     if (userInformation) {
  //       const userInfoObject = JSON.parse(userInformation);
  //       const refreshToken = userInfoObject.refreshToken ? userInfoObject.refreshToken[0] : null;
  //       const idToken = userInfoObject.idToken ? userInfoObject.idToken[0] : null;
  //       if (refreshToken) {
  //         setLocalStorageItem(REFRESH_TOKEN, refreshToken);
  //       } else {
  //         console.error('Refresh token not found in user information:', userInfoObject);
  //       }

  //       if(idToken){
  //         setLocalStorageItem(ID_TOKEN, idToken);
  //       }
  //       else{
  //         console.error('Id token not found in user information:', userInfoObject);
  //       }
  //     } else {
  //       console.error('User information not found in sessionStorage');
  //     }

    
  //}, []);


  // Handle successful authentication
  const handleSuccessfulLogin = useCallback((response) => {
    setLocalStorageItem(ACCESS_TOKEN, response.accessToken);
    sessionStorage.setItem("userInformation", JSON.stringify(response));
    setLocalStorageItem("userInformation", JSON.stringify(response));
    
    setIsLoading(false);
    setError(null);
    
    if(sessionFeedbackURL){
      navigate(sessionFeedbackURL);
      setSessionFeedbackUrl(null);
    } else {
      navigate(dashboardURL);
    }
  }, [navigate, sessionFeedbackURL]);

  // Main login function with fallback mechanism
  const loginAzureAD = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Use authentication strategy based on browser/platform detection
      if (authStrategy.preferRedirect) {
        console.log(`Using redirect authentication: ${authStrategy.reason}`);
        await instance.loginRedirect(loginRequest);
        return; // loginRedirect doesn't return a promise that resolves with the token
      }

      // For other browsers, try popup first with fallback
      try {
        const response = await instance.loginPopup(loginRequest);
        handleSuccessfulLogin(response);
      } catch (popupError) {
        // If popup fails (likely blocked), fallback to redirect if available
        if (authStrategy.fallbackAvailable &&
            popupError instanceof BrowserAuthError && 
            (popupError.errorCode === 'popup_window_error' || 
             popupError.errorCode === 'empty_window_error' ||
             popupError.errorCode === 'user_cancelled')) {
          
          console.log('Popup failed, falling back to redirect authentication');
          await instance.loginRedirect(loginRequest);
          return;
        }
        throw popupError; // Re-throw if it's a different error
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Authentication error:', error);
      
      // Set user-friendly error message
      if (error instanceof InteractionRequiredAuthError) {
        setError('Authentication required. Please try logging in again.');
      } else if (error instanceof BrowserAuthError) {
        setError('Browser compatibility issue. Please try a different browser or enable popups.');
      } else {
        setError('Login failed. Please try again or contact support if the problem persists.');
      }
    }
  }, [instance, handleSuccessfulLogin]);


  // Handle redirect response on page load
  useEffect(() => {
    const handleRedirectResponse = async () => {
      try {
        const response = await instance.handleRedirectPromise();
        if (response) {
          handleSuccessfulLogin(response);
        }
      } catch (error) {
        console.error('Redirect response error:', error);
        setError('Authentication failed after redirect. Please try again.');
      }
    };

    handleRedirectResponse();
  }, [instance, handleSuccessfulLogin]);

  const logoutAzureAD = useCallback(() => {
    instance
    .logoutPopup(logoutRequest)
    .then(() => {
      navigate(indexURL);
      localStorage.clear();
      })
      .catch((e) => {
        console.log(e);
      });
  }, [instance, navigate]);

  return {
    loginAzureAD, // New main login function
    loginAzureADPopup: loginAzureAD, // Maintain backward compatibility
    logoutAzureAD,
    isLoading,
    error,
    clearError: () => setError(null),
    browserInfo,
    authStrategy,
    compatibilityMessages,
    // Backward compatibility
    isSafari: browserInfo.isSafari,
    isMacOS: browserInfo.isMacOS,
  };
};
